import {
  AddOutlined,
  CheckOutlined,
  HourglassEmpty,
  KeyboardArrowRightOutlined,
} from "@mui/icons-material";
import { Box, Card, Chip, Collapse, IconButton, Tooltip } from "@mui/material";
import { green, grey, orange, red } from "@mui/material/colors";
import { every, isNil, size, some } from "lodash";
import groupBy from "lodash/groupBy";
import { memo, useCallback, useMemo, useState } from "react";
import { fileDocGroupAndTypes } from "../../constants/fileDocGroupAndTypes";
import FlxGrid from "../../_src_shared/components/layout/FlxGrid";
import Flx from "../layout/Flx";
import BasicFilesTable from "../tables/BasicFilesTable";
import Txt from "../typography/Txt";
import UploadLoanFileModal from "../modals/common/UploadLoanFileModal";

export const getRequiredDocGroupsAndTypes = (loan) => {
  const loanPurpose = loan?.loanPurpose; // 'Purchase' or something else
  const loanProductType = loan?.loanProductType; // e.g. 'Fix and Flip'
  const subjectProperties = loan?.subjectProperties || [];

  const TARGET_DOC_GROUPS_AND_TYPES = fileDocGroupAndTypes; // fileDocGroupAndTypesAll
  // Helper: find which group a doc type belongs to
  const findGroupForType = (docType) => {
    for (const [group, types] of Object.entries(TARGET_DOC_GROUPS_AND_TYPES)) {
      if (types.includes(docType)) return group;
    }
    return null;
  };

  // Collect required types (set to avoid dupes)
  const requiredTypes = new Set();

  // ---- Always-required docs per all_loan_required_docs ----
  requiredTypes.add("Bank Statement");

  // minimum_property_docs
  requiredTypes.add("Appraisal PDF");
  requiredTypes.add("Preliminary Title Policy");

  // minimum_entity_docs - FIXED: removed "Articles of Incorporation"
  requiredTypes.add("Articles of Organization");
  requiredTypes.add("COGS");
  requiredTypes.add("EIN");
  requiredTypes.add("Operating Agreement");

  // minimum_borrower_docs
  requiredTypes.add("Background Report");
  requiredTypes.add("Borrower OFAC Report");
  requiredTypes.add("Credit Report");
  requiredTypes.add("Loan Application");

  // Borrower Identification requirement (either Photo ID or Passport)
  requiredTypes.add("Passport");
  requiredTypes.add("Photo Identification");

  // Borrower Experience - add all subtypes from the group
  // Python checks for any "Borrower Experience" doc in doc_types
  const borrowerExpDocs = [
    "Borrower Experience - HUDs, Loan Docs",
    "Borrower Experience - Personal Financial Statement",
    "Borrower Experience - Property Reports",
    "Borrower Experience - REO Schedule",
    "Borrower Experience - Sourcing Entity Docs",
    "Borrower Experience - Track Record",
  ];
  borrowerExpDocs.forEach((doc) => requiredTypes.add(doc));

  // ---- Purpose-specific docs ----
  if (loanPurpose === "Purchase") {
    requiredTypes.add("Purchase Contract");
  } else {
    // Refinance or other
    requiredTypes.add("Payoff Letter");
    requiredTypes.add("Verification of Mortgage");
  }

  // ---- Product-specific docs (fix_and_flip_required_docs) ----
  const isFixAndFlip =
    typeof loanProductType === "string" &&
    loanProductType.toLowerCase().includes("fix") &&
    loanProductType.toLowerCase().includes("flip");

  const feasibilityRequired = subjectProperties.some(
    (sp) => sp?.feasibility_required || sp?.feasibilityRequired,
  );

  if (isFixAndFlip) {
    requiredTypes.add("Budget");

    if (feasibilityRequired) {
      // FIXED: Use actual doc type from Third Party Reports
      requiredTypes.add("Project Feasibility Analysis");
    }
  }

  // ---- Build group -> types[] structure ----
  const requiredByGroup = {};

  [...requiredTypes].forEach((docType) => {
    const group = findGroupForType(docType);

    // Skip if doc type not found in master list
    if (!group) {
      // console.warn(
      //   `Doc type "${docType}" not found in fileDocGroupAndTypesAll`,
      // );
      return;
    }

    if (!requiredByGroup[group]) requiredByGroup[group] = [];
    requiredByGroup[group].push(docType);
  });

  // ---- Alphabetize doc types within each group ----
  for (const group of Object.keys(requiredByGroup)) {
    requiredByGroup[group] = requiredByGroup[group]
      .filter((v, i, a) => a.indexOf(v) === i) // de-dupe
      .sort((a, b) => a.localeCompare(b));
  }

  // ---- Alphabetize groups (keys) in final returned object ----
  const sortedRequiredByGroup = Object.keys(requiredByGroup)
    .sort((a, b) => a.localeCompare(b))
    .reduce((acc, group) => {
      acc[group] = requiredByGroup[group];
      return acc;
    }, {});

  return sortedRequiredByGroup;
};

const RED_DARK = red[800];
// const RED_LIGHT = "rgb(255 235 238 / 50%)";
const RED_LIGHT = red[50];
const GREEN_DARK = green[900];
const GREEN_LIGHT = green[50];
const BLACK_TEXT = "#232a31";
// const _ORANG E_DARK = orange[900];
// const ORANGE_LIGHT = "rgb(255 243 224 / 50%)";
// const ORANGE_LIGHT = orange[50];
const ORANGE_LIGHT = orange[50];

const LoanRequiredDocsRenderer = ({ loan, files = [], width = "420px" }) => {
  const [showAll, setShowAll] = useState(false);
  const rootStyles = useMemo(() => {
    return {
      p: 2,
      borderBottom: `1px solid ${grey[300]}`,
    };
  }, []);

  // Index files by docGroup -> docType for quick lookup
  const filesByGroupThenType = useMemo(() => {
    const byGroup = groupBy(files, (f) => f.docGroup || "Unknown");
    const result = {};
    Object.keys(byGroup).forEach((group) => {
      result[group] = groupBy(byGroup[group], (f) => f.docType || "Unknown");
    });
    return result;
  }, [files]);

  const docGroupsAndTypes = useMemo(() => {
    if (showAll) {
      return fileDocGroupAndTypes;
    }

    // return requiredFileDocGroupAndTypes;
    return getRequiredDocGroupsAndTypes(loan);
  }, [showAll, loan]);

  return (
    <FlxGrid className="loan-required-docs-panel" width={width} gap={1.5}>
      {Object.entries(docGroupsAndTypes).map(([docGroup, docTypes]) => (
        <DocGroupSection
          key={docGroup}
          docGroup={docGroup}
          docTypes={docTypes}
          dealId={loan?._id}
          filesByType={filesByGroupThenType[docGroup] || {}}
        />
      ))}
    </FlxGrid>
  );
};

const DocGroupSection = ({ docGroup, docTypes, dealId, filesByType }) => {
  const [show, setShow] = useState(false);

  const completedCount = useMemo(
    () => docTypes.filter((dt) => (filesByType[dt] || []).length > 0).length,
    [docTypes, filesByType],
  );

  // Calculate status to share between chips
  const groupStatus = useMemo(() => {
    const filesPerType = docTypes.map((dt) => filesByType[dt] || []);
    const anyMissingType = some(filesPerType, (arr) => size(arr) === 0);

    if (anyMissingType) {
      return "incomplete";
    }

    const allAcceptedAcrossTypes = every(filesPerType, (arr) =>
      every(arr, (f) => f?.documentStatus === "Accepted"),
    );

    if (allAcceptedAcrossTypes) {
      return "completed";
    }

    return "awaiting";
  }, [docTypes, filesByType]);

  return (
    <ChecklistSectionCard sx={{ mb: 1, height: "max-content" }}>
      <CollapsibleHeader
        show={show}
        setShow={setShow}
        title={docGroup}
        // title={
        //   <Flx ac gap={1.5} wrap className={"aaaaa"}>
        //     {docGroup}
        //     <DocGroupProgressChip
        //       completedCount={completedCount}
        //       totalCount={docTypes.length}
        //       groupStatus={groupStatus}
        //     />
        //   </Flx>
        // }
        endContent={
          <Flx ac g={1}>
            {/* <DocGroupProgressChip
              completedCount={completedCount}
              totalCount={docTypes.length}
              groupStatus={groupStatus}
            /> */}
            <DocGroupProgressChip
              completedCount={completedCount}
              totalCount={docTypes.length}
              groupStatus={groupStatus}
            />
            <DocGroupStatusChip
              docTypes={docTypes}
              filesByType={filesByType}
              groupStatus={groupStatus}
            />
          </Flx>
        }
      />
      <Collapse in={show}>
        <Box sx={{ p: 1.5 }}>
          <RequiredDocTypesList
            docGroup={docGroup}
            dealId={dealId}
            docTypes={docTypes}
            filesByType={filesByType}
          />
        </Box>
      </Collapse>
    </ChecklistSectionCard>
  );
};

const DocGroupStatusChip = ({ groupStatus }) => {
  if (groupStatus === "incomplete") {
    return (
      <Chip
        size="small"
        label="Incomplete"
        sx={{
          background: RED_LIGHT,
          color: BLACK_TEXT,
          fontWeight: 400,
          fontStyle: "italic",
        }}
      />
    );
  }

  if (groupStatus === "completed") {
    return (
      <Chip
        icon={
          <CheckOutlined
            sx={{
              color: `${GREEN_DARK} !important`,
              fontSize: "11px !important",
            }}
          />
        }
        size="small"
        label="Completed"
        sx={{
          pl: 0.2,
          background: GREEN_LIGHT,
          color: GREEN_DARK,
          fontWeight: 400,
          fontStyle: "italic",
        }}
      />
    );
  }

  if (groupStatus === "awaiting") {
    return (
      <Chip
        size="small"
        label="Received & Pending"
        sx={{
          background: ORANGE_LIGHT,
          color: BLACK_TEXT,
          fontWeight: 400,
          fontStyle: "italic",
        }}
      />
    );
  }
};

const DocGroupProgressChip = ({ completedCount, totalCount, groupStatus }) => {
  // Determine chip styling based on groupStatus
  const getChipProps = () => {
    if (groupStatus === "completed") {
      return {
        color: "success",
        variant: "filled",
        sx: {
          background: GREEN_LIGHT,
          color: GREEN_DARK,
          fontWeight: 600,
        },
      };
    }

    // if (groupStatus === "awaiting") {
    //   return {
    //     // variant: "filled",
    //     color: "default",
    //     variant: "outlined",
    //     sx: {
    //       // background: ORANGE_LIGHT,
    //       color: BLACK_TEXT,
    //     },
    //   };
    // }

    // incomplete or default
    return {
      color: "default",
      variant: "outlined",
      sx: {},
    };
  };

  const chipProps = getChipProps();

  return (
    <Tooltip
      title={`${completedCount} of ${totalCount} document types received`}
    >
      <Chip
        label={`${completedCount} / ${totalCount}`}
        {...chipProps}
        size="small"
        sx={{
          fontSize: "12px",
          minWidth: 24,
          flexGrow: 0,
          ...chipProps.sx,
        }}
      />
    </Tooltip>
  );
};

const RequiredDocTypesList = ({ docGroup, docTypes, filesByType, dealId }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {docTypes.map((docType) => (
        <DocTypeFilesSection
          key={docType}
          docType={docType}
          dealId={dealId}
          docGroup={docGroup}
          filesForType={filesByType[docType] || []}
        />
      ))}
    </Box>
  );
};

const DocTypeFilesSection = ({ docGroup, docType, filesForType, dealId }) => {
  const [show, setShow] = useState(false);
  const hasFiles = filesForType?.length > 0;

  return (
    <Box
      sx={{
        border: `1px solid ${grey[300]}`,
        borderRadius: 1.5,
        background: "#ffffff",
      }}
    >
      <CollapsibleHeader
        show={show}
        disabled={!hasFiles}
        setShow={setShow}
        size="small"
        title={
          <Flx ac gap={1}>
            {docType}
            <DocTypeFilesSectionChip
              filesForType={filesForType}
              hasFiles={hasFiles}
            />
          </Flx>
        }
        endContent={
          <FileUploaderIconButton
            tooltip={`Upload ${docType}`}
            docGroup={docGroup}
            docType={docType}
            dealId={dealId}
          />
        }
      />
      {hasFiles && show && (
        <>
          <BasicFilesTable
            showStatus
            hideDocType
            hideDocGroup
            headerHeight={0}
            // headerHeight={48}
            // showUploadDate
            // defaultView
            files={filesForType}
          />
          {/* <LoanFilesDisplayTable showStatus rowData={filesForType} /> */}
        </>
      )}
    </Box>
  );
};

const DocTypeFilesSectionChip = ({ filesForType, hasFiles }) => {
  if (!hasFiles) {
    return (
      <Chip
        size="small"
        label="Missing"
        sx={{
          background: RED_LIGHT,
          color: BLACK_TEXT,
          fontWeight: 400,
          fontStyle: "italic",
        }}
      />
    );
  }

  // Check if at least one is accepted
  const hasAccepted = some(
    filesForType,
    (f) => f?.documentStatus === "Accepted",
  );

  // Count only accepted files
  const acceptedCount = filesForType.filter(
    (f) => f?.documentStatus === "Accepted",
  ).length;

  const label = hasAccepted
    ? `Completed (${acceptedCount})`
    : `Pending (${size(filesForType)})`;

  const bg = hasAccepted ? GREEN_LIGHT : ORANGE_LIGHT;
  const fg = hasAccepted ? GREEN_DARK : BLACK_TEXT;

  return (
    <Chip
      size="small"
      label={label}
      icon={
        hasAccepted ? (
          <CheckOutlined
            sx={{
              color: `${GREEN_DARK} !important`,
              fontSize: "11px !important",
            }}
          />
        ) : (
          <HourglassEmpty
            sx={{
              color: `${BLACK_TEXT} !important`,
              fontSize: "11px !important",
            }}
          />
        )
      }
      sx={{
        background: bg,
        color: fg,
        pl: 0.3,
        fontWeight: 400,
        lineHeight: 1.2,
        fontStyle: "italic",
      }}
    />
  );
};

const CollapsibleHeader = ({
  show,
  setShow,
  title,
  titleSx,
  endContent,
  disabled,
  size = "medium",
  sx,
}) => {
  const iconSizeMap = {
    small: "18px",
    medium: "18px",
    large: "18px",
  };
  const titleSizeMap = {
    small: "13px",
    medium: "15px",
    large: "18px",
  };
  const titleWeighteMap = {
    small: 400,
    medium: 500,
    large: 900,
  };
  return (
    <Flx
      fw
      jb
      ac
      sx={{
        // px: 1.5,
        // py: 2,
        //
        pr: 1.5,
        borderBottom: `1px solid ${grey[200]}`,
        "&:hover": {
          background: disabled ? "transparent" : grey[50],
        },
        ...sx,
      }}
    >
      <Box
        onClick={() => !disabled && setShow((s) => !s)}
        sx={{
          flexGrow: 1,
          cursor: disabled ? "default" : "pointer",
          pl: 1.5,
          py: 2,
        }}
      >
        <Flx ac gap={0.5}>
          <KeyboardArrowRightOutlined
            // fontSize="small"
            className="thin header-collapse-icon"
            sx={{
              fontSize: iconSizeMap[size],
              opacity: disabled ? 0.45 : 1,
              transition: "transform 0.3s",
              transform: show ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />

          <Flx ac jb sx={{ flexGrow: 1 }} gap={1.5}>
            <Txt
              sx={{
                fontSize: titleSizeMap[size],
                fontWeight: titleWeighteMap[size],

                ...titleSx,
              }}
            >
              {title}
            </Txt>
            {endContent}
          </Flx>
        </Flx>
      </Box>
    </Flx>
  );
};

const FileUploaderIconButton = ({ docGroup, docType, dealId }) => {
  const [addFiles, setAddFiles] = useState(false);
  return (
    <>
      <Tooltip title={`Upload ${docType}`}>
        <IconButton onClick={() => setAddFiles(true)} size="small">
          <AddOutlined />
        </IconButton>
      </Tooltip>
      {addFiles ? (
        <UploadLoanFileModal
          show={addFiles}
          dealId={dealId}
          title={`Upload ${docType}`}
          onClose={setAddFiles}
          docGroup={docGroup}
          docType={docType}
        />
      ) : null}
    </>
  );
};

export const ChecklistSectionCard = memo(({ children, sectionStats, sx }) => {
  const getColor = useCallback((stats) => {
    if (isNil(stats)) return grey[800];
    if (stats.isCompleted) return GREEN_DARK;
    if (stats.failedCount > 0) return red[700];
    if (stats.exceptionRequestedCount > 0) return BLACK_TEXT;
    return grey[800];
  }, []);

  const styles = useMemo(() => {
    return {
      mb: 2,
      p: 0,
      boxShadow: "none",
      border: "1px solid #dde1e7",
      background: "#ffffff",
      transition: "100ms",
      "h3.checklist-section-title": {
        color: getColor(sectionStats),
      },
      "&:hover": {
        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      },
      ...sx,
    };
  }, [sx, sectionStats, getColor]);

  return (
    <Card variant="outlined" sx={styles}>
      {children}
    </Card>
  );
});

export default LoanRequiredDocsRenderer;
